import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useInvoicingStatusReport } from '../../hooks/useReports';
import ReportFilters from '../../components/domain/ReportFilters';
import ReportTable from '../../components/domain/ReportTable';
import { Spinner } from '../../components/common/Spinner';
import { formatCurrency, formatHours, formatDisplayDate } from '../../utils/format';
import { MoneyMetricLabel } from '../../components/common/MoneyMetricLabel';
import type { InvoicingStatusRow } from '../../services/api/reports';

type InvoicedFilter = 'all' | 'invoiced' | 'uninvoiced';

function parseInvoicedFilter(value: string | null): InvoicedFilter {
  if (value === 'invoiced' || value === 'uninvoiced') return value;
  return 'all';
}

export default function InvoicingStatusReportPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const filtersFromUrl = useMemo(() => {
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    if (from && to) return { from, to };
    return null;
  }, [searchParams]);

  const [filters, setFilters] = useState<{ from: string; to: string } | null>(filtersFromUrl);
  const [invoicedFilter, setInvoicedFilter] = useState<InvoicedFilter>(
    parseInvoicedFilter(searchParams.get('invoiced'))
  );
  const { data: report, isLoading, error } = useInvoicingStatusReport(filters);

  const handleApply = useCallback((f: { from: string; to: string }) => {
    setFilters(f);
    setSearchParams((prev) => {
      prev.set('from', f.from);
      prev.set('to', f.to);
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const handleInvoicedFilter = useCallback((value: InvoicedFilter) => {
    setInvoicedFilter(value);
    setSearchParams((prev) => {
      if (value === 'all') {
        prev.delete('invoiced');
      } else {
        prev.set('invoiced', value);
      }
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const filteredRows = useMemo(() => {
    if (!report) return [];
    if (invoicedFilter === 'invoiced') return report.rows.filter((r) => r.invoiceId !== null);
    if (invoicedFilter === 'uninvoiced') return report.rows.filter((r) => r.invoiceId === null);
    return report.rows;
  }, [report, invoicedFilter]);

  const columns = [
    {
      key: 'date',
      header: 'Date',
      render: (row: InvoicingStatusRow) => <span className="text-gray-900">{formatDisplayDate(row.date)}</span>,
      sortValue: (row: InvoicingStatusRow) => row.date,
    },
    {
      key: 'project',
      header: 'Project',
      render: (row: InvoicingStatusRow) => <span className="text-gray-900">{row.projectName}</span>,
      sortValue: (row: InvoicingStatusRow) => row.projectName,
    },
    {
      key: 'user',
      header: 'User',
      render: (row: InvoicingStatusRow) => <span className="text-gray-700">{row.userName}</span>,
      sortValue: (row: InvoicingStatusRow) => row.userName,
    },
    {
      key: 'task',
      header: 'Task',
      render: (row: InvoicingStatusRow) => <span className="text-gray-500">{row.task || '-'}</span>,
      sortValue: (row: InvoicingStatusRow) => row.task || '',
    },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right' as const,
      render: (row: InvoicingStatusRow) => <span className="text-gray-700">{formatHours(row.hours)}</span>,
      sortValue: (row: InvoicingStatusRow) => row.hours,
    },
    {
      key: 'rate',
      header: 'Rate',
      align: 'right' as const,
      render: (row: InvoicingStatusRow) => <span className="text-gray-700">{formatCurrency(row.billingRate)}</span>,
      sortValue: (row: InvoicingStatusRow) => row.billingRate,
    },
    {
      key: 'amount',
      header: <MoneyMetricLabel metric="earned" align="right" />,
      align: 'right' as const,
      render: (row: InvoicingStatusRow) => <span className="font-medium text-gray-900">{formatCurrency(row.totalBilling)}</span>,
      sortValue: (row: InvoicingStatusRow) => row.totalBilling,
    },
    {
      key: 'invoice',
      header: 'Invoice',
      render: (row: InvoicingStatusRow) =>
        row.invoiceId !== null ? (
          <Link
            to={`/invoices/${row.invoiceId}`}
            className="text-primary-600 hover:text-primary-800 hover:underline text-sm"
          >
            #{row.invoiceId}
          </Link>
        ) : (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
            Uninvoiced
          </span>
        ),
      sortValue: (row: InvoicingStatusRow) => row.invoiceName || (row.invoiceId !== null ? `#${row.invoiceId}` : 'zzz'),
    },
  ];

  const filterButtons: { label: string; value: InvoicedFilter }[] = [
    { label: 'All', value: 'all' },
    { label: 'Invoiced', value: 'invoiced' },
    { label: 'Uninvoiced', value: 'uninvoiced' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/reports')}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            &larr; Back to Reports
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">Invoicing Status Report</h1>
        </div>
      </div>

      <ReportFilters
        onApply={handleApply}
        initialFrom={filtersFromUrl?.from}
        initialTo={filtersFromUrl?.to}
      >
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium text-gray-700 mr-1">Invoiced</label>
          {filterButtons.map((btn) => (
            <button
              key={btn.value}
              onClick={() => handleInvoicedFilter(btn.value)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                invoicedFilter === btn.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {btn.label}
            </button>
          ))}
        </div>
      </ReportFilters>

      {error && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          Failed to load report. Please try again.
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}

      {report && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-medium text-gray-500">Total Hours</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{formatHours(report.totals.totalHours)}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-medium text-gray-500">
                <MoneyMetricLabel metric="earned" />
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(report.totals.totalBilling)}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-medium text-gray-500">
                <MoneyMetricLabel metric="invoiced" />
              </p>
              <p className="mt-1 text-2xl font-semibold text-green-700">{formatCurrency(report.totals.invoicedAmount)}</p>
              <p className="text-xs text-gray-500">{report.totals.invoicedCount} entries &middot; {formatHours(report.totals.invoicedHours)} hrs</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-medium text-gray-500">Uninvoiced</p>
              <p className="mt-1 text-2xl font-semibold text-amber-700">{formatCurrency(report.totals.uninvoicedAmount)}</p>
              <p className="text-xs text-gray-500">{report.totals.uninvoicedCount} entries &middot; {formatHours(report.totals.uninvoicedHours)} hrs</p>
            </div>
          </div>

          {/* WR-03: Invoiced + Uninvoiced partition Revenue (consumption) again,
              but per-row Billing below stays the entry write-time snapshot
              (0 for package entries) — the footer includes project-level
              package revenue the rows cannot show. */}
          <p className="text-xs text-gray-500">
            Earned = Invoiced + Uninvoiced. It includes fixed-price and package
            (balíček) revenue recognized at project level, which per-entry Billing rows below show
            as 0 &mdash; the table footer therefore may exceed the sum of the visible Billing cells.
          </p>

          <ReportTable
            columns={columns}
            rows={filteredRows}
            getRowKey={(row) => row.id}
            defaultSort={{ key: 'date', direction: 'asc' }}
            footer={
              <tr className="font-medium">
                <td className="px-6 py-3 text-sm text-gray-900" colSpan={4}>Total</td>
                <td className="px-6 py-3 text-sm text-gray-900 text-right">{formatHours(report.totals.totalHours)}</td>
                <td className="px-6 py-3 text-sm text-gray-900 text-right"></td>
                <td className="px-6 py-3 text-sm text-gray-900 text-right">{formatCurrency(report.totals.totalBilling)}</td>
                <td className="px-6 py-3 text-sm text-gray-900"></td>
              </tr>
            }
          />
        </>
      )}
    </div>
  );
}
